import { useFormik } from 'formik'
import React, { useEffect, useState } from 'react'
import ModalComponent from '..'
import { SelectFulfilmentCenterSchema } from '../../../validation/validate'
import { Button } from '../../button'

import { PRIMARY_COLOR } from '../../../constants'
import { deepCopy } from '../../../helpers'
import { useAppSelector } from '../../../hooks'
import { useGetFulfilmentCentersQuery } from '../../../redux-toolkits/vendors/vendors.slice'
import {
  FulfilmentCentre,
  FulfilmentCentreRole,
} from '../../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { QueryProps } from '../../../redux-toolkits/shared/shared.type'
import { EmptyState } from '../../emptyState/emptyState'
import { Checkbox } from '../../inputs/Checkbox'
import { SearchInput } from '../../inputs/searchInput'
import { LoadingOval } from '../../spinner/Spinner'

export const SelectFulfilmentCenterModal: React.FC<{
  open: boolean
  setOpen: (v: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: FulfilmentCentre[], resetForm: () => void) => void
  maxSelection?: number
  selectedCenters?: FulfilmentCentre[]
  loading?: boolean
  includeParentStorefronts?: boolean
  physicalOnly?: boolean
  useAssignedCentersOnly?: boolean
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = (val) => {},
  maxSelection = undefined,
  selectedCenters,
  loading = false,
  useAssignedCentersOnly = false,
  physicalOnly = true,
  includeParentStorefronts = false,
}) => {
  const [queryParams, setQueryParams] = useState<QueryProps>({
    search: undefined,
  })
  const [centersData, setCentersData] = useState<FulfilmentCentre[]>([])

  const { user } = useAppSelector((state) => state.auth)

  const { data: dataRespose, isFetching: fetchingCenters } =
    useGetFulfilmentCentersQuery(
      {
        ...queryParams,
        search: queryParams.search,
      },
      {
        skip: !open || (useAssignedCentersOnly && !includeParentStorefronts),
        // refetchOnMountOrArgChange: true,
      },
    )

  const { values, handleSubmit, isValid, resetForm, setFieldValue } = useFormik(
    {
      initialValues: {
        centers: (selectedCenters ?? []).filter(
          (centre) =>
            !physicalOnly ||
            includeParentStorefronts ||
            centre.role !== FulfilmentCentreRole.PARENT,
        ),
      } as {
        centers: FulfilmentCentre[]
      },
      enableReinitialize: true,
      validationSchema: SelectFulfilmentCenterSchema,
      onSubmit: (values, { resetForm }) => {
        onSuccessCallback(values.centers, () => {
          resetForm()
        })
      },
    },
  )

  useEffect(() => {
    const physicalCentres = (user?.fulfillmentCenter ?? []).filter(
      (centre) => centre.role !== FulfilmentCentreRole.PARENT,
    )
    if (useAssignedCentersOnly) {
      const parents =
        includeParentStorefronts && physicalCentres.length > 0
          ? (dataRespose?.data.docs ?? []).filter(
              (centre) =>
                centre.role === FulfilmentCentreRole.PARENT &&
                centre.isActive !== false,
            )
          : []
      setCentersData([...parents, ...physicalCentres])
      return
    }
    setCentersData(
      (dataRespose?.data.docs ?? []).filter(
        (centre) =>
          !physicalOnly ||
          includeParentStorefronts ||
          centre.role !== FulfilmentCentreRole.PARENT,
      ),
    )
  }, [
    dataRespose,
    useAssignedCentersOnly,
    user?.fulfillmentCenter,
    physicalOnly,
    includeParentStorefronts,
  ])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={'Select Fulfilment Center'}
      childrenClassName="md:!max-w-[420px]"
    >
      <form
        className="flex flex-col gap-2 min-h-[300px] justify-between"
        onSubmit={handleSubmit}
      >
        {useAssignedCentersOnly ? undefined : (
          <SearchInput
            searchFunction={(search) => {
              setQueryParams({
                ...queryParams,
                search: search || undefined,
                skip: 0,
              })
            }}
            placeholder="Search fulfilment center"
            className="!max-w-full"
          />
        )}

        <div className="flex-1 overflow-y-scroll">
          {fetchingCenters ? (
            <div className="w-full flex justify-center pt-10">
              <LoadingOval
                loaderWidth="25"
                loaderHeight="25"
                color={PRIMARY_COLOR}
              />
            </div>
          ) : (
            <div>
              {centersData.length > 0 ? (
                <div className="flex flex-col">
                  {centersData.map((center, idx) => {
                    return (
                      <Checkbox
                        disabled={
                          maxSelection !== undefined &&
                          values.centers.length >= maxSelection &&
                          !values.centers.map((c) => c._id).includes(center._id)
                        }
                        checkStatus={
                          values.centers
                            .map((c) => c._id)
                            ?.includes(center._id) ?? false
                        }
                        handleChange={(checked) => {
                          const centersCopy = deepCopy(values.centers)
                          if (checked) {
                            centersCopy.push(center)

                            setFieldValue('centers', centersCopy)
                          } else {
                            setFieldValue(
                              'centers',
                              centersCopy.filter(
                                (singleCenter) =>
                                  center._id !== singleCenter._id,
                              ),
                            )
                          }
                        }}
                        text={
                          center.role === FulfilmentCentreRole.PARENT
                            ? `${center.name} — assigned child centres`
                            : center.name
                        }
                        textPosition="right"
                        className="
                        py-3 border-b last:border-b-0
                        w-full
                        "
                        key={center._id}
                      />
                    )
                  })}
                </div>
              ) : (
                <EmptyState />
              )}
            </div>
          )}
        </div>

        <Button
          label="Proceed"
          type="button"
          onClick={() => {
            handleSubmit()
          }}
          className="sticky bottom-0"
          disabled={!isValid || loading}
          loading={loading}
          primary
        />
      </form>
    </ModalComponent>
  )
}
